import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Timer } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { formatarDataHora, formatarMinutos } from '@/lib/format';
import { useRascunho } from '@/lib/rascunho';
import { Modal } from '@/features/tickets/Modal';
import { AvisoDeFalha, AvisoDeRegistroMudado } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import {
  deCampoLocal,
  duracaoEmMinutos,
  paraCampoLocal,
  rotuloDeHorario,
} from '@/features/changes/calendario';
import {
  alterarJanela,
  chavesDeMudanca,
  criarJanela,
  excluirJanela,
  listarJanelas,
  obterJanela,
} from '@/features/changes/changes.api';
import {
  Campo,
  CLASSE_DE_CAMPO,
  EtiquetaDeJanela,
  EtiquetaDeRisco,
  EtiquetaDeTipoDeMudanca,
} from '@/features/changes/componentes';
import { LinkDaMudanca } from '@/features/changes/navegacao';
import {
  ROTULO_DE_JANELA,
  SITUACOES_DE_JANELA,
  type JanelaListada,
  type SituacaoDeJanela,
} from '@/features/changes/tipos';

/**
 * As janelas de mudança.
 *
 * Uma janela é compartilhada: sete mudanças podem estar penduradas na mesma
 * madrugada de sábado. Por isso mover uma janela é operação de peso — o
 * servidor arrasta `plannedStart`/`plannedEnd` de todas as mudanças dentro dela
 * na mesma transação, e **recusa** o movimento que jogaria alguma para dentro
 * de um congelamento sem exceção aprovada.
 *
 * A tela diz isso antes de a pessoa tentar, e mostra quem está dentro antes de
 * ela mover.
 */
export function JanelasPage() {
  const [criando, setCriando] = useState(false);
  const consulta = useQuery({ queryKey: chavesDeMudanca.janelas, queryFn: listarJanelas });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => setCriando(true)}
          icon={<CalendarPlus className="h-4 w-4" aria-hidden />}
        >
          Nova janela
        </Button>
      </div>

      {consulta.isPending ? (
        <SkeletonRegion label="Carregando janelas" className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </SkeletonRegion>
      ) : null}

      {consulta.isError ? (
        <AvisoDeFalha
          falha={classificarFalha(consulta.error)}
          aoRecarregar={() => void consulta.refetch()}
        />
      ) : null}

      {consulta.data?.length === 0 ? (
        <EmptyState
          icon={<Timer className="h-7 w-7" aria-hidden />}
          title="Nenhuma janela de mudança cadastrada"
          description="A janela é o período em que a manutenção pode acontecer. Sem nenhuma, o fluxo de mudança padrão e o de mudança normal barram o agendamento — os dois exigem janela para avançar."
          action={<Button size="sm" onClick={() => setCriando(true)}>Criar a primeira</Button>}
        />
      ) : null}

      <div className="space-y-3">
        {(consulta.data ?? []).map((janela) => (
          <CartaoDeJanela key={janela.id} janela={janela} />
        ))}
      </div>

      {criando ? <DialogoDeJanela aoFechar={() => setCriando(false)} /> : null}
    </div>
  );
}

function CartaoDeJanela({ janela }: { janela: JanelaListada }) {
  const [editando, setEditando] = useState(false);
  const [mostrandoConteudo, setMostrandoConteudo] = useState(false);
  const cliente = useQueryClient();
  const [falhaExclusao, setFalhaExclusao] = useState<Falha | null>(null);

  const excluir = useMutation({
    mutationFn: () => excluirJanela(janela.id),
    onMutate: () => setFalhaExclusao(null),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chavesDeMudanca.janelas }),
    onError: (erro) => setFalhaExclusao(classificarFalha(erro)),
  });

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {janela.name}
            <EtiquetaDeJanela situacao={janela.status} />
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 tabular-nums">
            <span>{formatarDataHora(janela.startsAt)}</span>
            <span aria-hidden>·</span>
            <span>{rotuloDeHorario(janela.startsAt, janela.endsAt)}</span>
            <span aria-hidden>·</span>
            <span>{formatarMinutos(duracaoEmMinutos(janela.startsAt, janela.endsAt))}</span>
          </span>
        }
        action={
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditando(true)}>
              Editar
            </Button>
            {janela._count.changes === 0 && janela._count.releases === 0 ? (
              <Button
                variant="danger"
                size="sm"
                loading={excluir.isPending}
                onClick={() => excluir.mutate()}
              >
                Excluir
              </Button>
            ) : null}
          </div>
        }
      />
      <CardBody className="space-y-3">
        {falhaExclusao ? <AvisoDeFalha falha={falhaExclusao} /> : null}
        {janela.description ? (
          <p className="text-sm text-muted-foreground">{janela.description}</p>
        ) : null}
        {janela.recurrenceNote ? (
          <p className="text-xs text-muted-foreground">
            Recorrência (texto livre, sem materialização automática nesta fase):{' '}
            <span className="font-medium text-foreground">{janela.recurrenceNote}</span>
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {janela._count.changes} mudança(s) · {janela._count.releases} versão(ões) de entrega
          </span>
          {janela._count.changes > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMostrandoConteudo((atual) => !atual)}
              aria-expanded={mostrandoConteudo}
            >
              {mostrandoConteudo ? 'Ocultar o que está dentro' : 'Ver o que está dentro'}
            </Button>
          ) : null}
        </div>

        {mostrandoConteudo ? <ConteudoDaJanela windowId={janela.id} /> : null}
      </CardBody>

      {editando ? <DialogoDeJanela janela={janela} aoFechar={() => setEditando(false)} /> : null}
    </Card>
  );
}

/** Quem está agendado nesta janela — a lista que se olha antes de movê-la. */
function ConteudoDaJanela({ windowId }: { windowId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeMudanca.janela(windowId),
    queryFn: () => obterJanela(windowId),
  });

  if (consulta.isPending) return <Skeleton className="h-16 w-full" />;
  if (consulta.isError) {
    return (
      <AvisoDeFalha
        falha={classificarFalha(consulta.error)}
        aoRecarregar={() => void consulta.refetch()}
      />
    );
  }

  return (
    <ul className="divide-y divide-border rounded-md border border-border px-3">
      {consulta.data.changes.map((membro) => (
        <li key={membro.ticketId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
          <LinkDaMudanca
            ticketId={membro.ticketId}
            className="font-mono text-xs font-semibold text-primary hover:underline"
          >
            {membro.ticket.number}
          </LinkDaMudanca>
          <span className="min-w-0 flex-1 truncate">{membro.ticket.subject}</span>
          <EtiquetaDeTipoDeMudanca tipo={membro.changeType} />
          <EtiquetaDeRisco nivel={membro.riskLevel} />
        </li>
      ))}
      {consulta.data.releases.map((release) => (
        <li key={release.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
          <span className="font-mono text-xs font-semibold">{release.code}</span>
          <span className="min-w-0 flex-1 truncate">{release.name}</span>
          <span className="text-xs text-muted-foreground">versão de entrega</span>
        </li>
      ))}
    </ul>
  );
}

// ── formulário ─────────────────────────────────────────────────────────────

function DialogoDeJanela({
  janela,
  aoFechar,
}: {
  janela?: JanelaListada;
  aoFechar: () => void;
}) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  /**
   * O formulário inteiro num rascunho — ver `@/lib/rascunho`.
   *
   * Aqui a marca é a própria `version`, e não uma assinatura de conteúdo: este
   * diálogo é o único formulário que edita a janela, então qualquer
   * incremento de versão é alteração do que ele mostra.
   *
   * Sem isto era a sobrescrita silenciosa do lado da administração: a listagem
   * atrás do diálogo refaz sozinha, a `janela` da propriedade vira a versão
   * nova, e salvar mandaria essa versão junto com o texto velho — apagando,
   * sem 409, o horário que outra pessoa acabou de corrigir. Numa janela com
   * sete mudanças penduradas, isso move sete manutenções.
   */
  const rascunho = useRascunho(
    () => ({
      nome: janela?.name ?? '',
      descricao: janela?.description ?? '',
      inicio: paraCampoLocal(janela?.startsAt),
      fim: paraCampoLocal(janela?.endsAt),
      recorrencia: janela?.recurrenceNote ?? '',
      situacao: (janela?.status ?? 'PLANNED') as SituacaoDeJanela,
    }),
    janela?.version ?? 0,
  );
  const { nome, descricao, inicio, fim, recorrencia, situacao } = rascunho.valor;
  const alterar = (parcial: Partial<typeof rascunho.valor>) =>
    rascunho.definir((atual) => ({ ...atual, ...parcial }));

  const inicioIso = deCampoLocal(inicio);
  const fimIso = deCampoLocal(fim);
  const intervaloValido =
    inicioIso !== null && fimIso !== null && new Date(fimIso) > new Date(inicioIso);
  const incompleto = rascunho.desatualizado || nome.trim().length < 3 || !intervaloValido;

  const gravar = useMutation({
    mutationFn: async () => {
      if (inicioIso === null || fimIso === null) throw new Error('Datas inválidas.');
      const corpo = {
        name: nome.trim(),
        startsAt: inicioIso,
        endsAt: fimIso,
      };
      /**
       * Na edição, descrição e recorrência vão **sempre**, inclusive vazias.
       *
       * Omitir a chave quando o campo está em branco é o que impedia apagar as
       * duas: o servidor só grava o que recebe (`patch.description !==
       * undefined`), então limpar o campo e salvar devolvia "sucesso" com o
       * texto antigo intacto — e ele reaparecia na recarga seguinte. Cadeia
       * vazia é o valor que o DTO aceita para "não há".
       *
       * Na criação é o contrário: o serviço faz `input.description ?? null`, e
       * cadeia vazia não é nula. Mandá-la faria toda janela nova nascer com
       * `''` onde o tipo diz `string | null`.
       */
      return janela
        ? alterarJanela(janela.id, {
            ...corpo,
            description: descricao.trim(),
            recurrenceNote: recorrencia.trim(),
            status: situacao,
            version: rascunho.marca as number,
          })
        : criarJanela({
            ...corpo,
            ...(descricao.trim() ? { description: descricao.trim() } : {}),
            ...(recorrencia.trim() ? { recurrenceNote: recorrencia.trim() } : {}),
          });
    },
    onMutate: () => setFalha(null),
    onSuccess: () => {
      // Mover a janela move as mudanças dentro dela: a raiz de mudanças e a de
      // chamados precisam recarregar juntas, senão o calendário mostra o
      // horário novo e a ficha da mudança continua com o antigo.
      void cliente.invalidateQueries({ queryKey: chavesDeMudanca.raiz });
      void cliente.invalidateQueries({ queryKey: ['chamados'] });
      aoFechar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={janela ? `Editar "${janela.name}"` : 'Nova janela de mudança'}
      descricao={
        janela && janela._count.changes > 0
          ? `Esta janela tem ${janela._count.changes} mudança(s) dentro. Mover o horário move todas elas — e o servidor recusa o movimento que jogaria alguma para dentro de um congelamento.`
          : 'O período em que a manutenção pode acontecer. Uma janela pode receber várias mudanças.'
      }
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!incompleto) gravar.mutate();
        }}
      >
        <Campo rotulo="Nome" htmlFor="janela-nome">
          <input
            id="janela-nome"
            value={nome}
            onChange={(evento) => alterar({ nome: evento.target.value })}
            className={CLASSE_DE_CAMPO}
            placeholder="Manutenção mensal — 1ª terça"
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Começa em" htmlFor="janela-inicio">
            <input
              id="janela-inicio"
              type="datetime-local"
              value={inicio}
              onChange={(evento) => alterar({ inicio: evento.target.value })}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
          <Campo
            rotulo="Termina em"
            htmlFor="janela-fim"
            dica={
              intervaloValido || fim === ''
                ? undefined
                : 'O fim precisa vir depois do começo. Janela de duração zero não é janela.'
            }
          >
            <input
              id="janela-fim"
              type="datetime-local"
              value={fim}
              onChange={(evento) => alterar({ fim: evento.target.value })}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </div>

        <Campo
          rotulo="Descrição"
          htmlFor="janela-descricao"
          dica="Apagar o texto e salvar apaga mesmo — o campo em branco é enviado como tal."
        >
          <textarea
            id="janela-descricao"
            rows={2}
            value={descricao}
            onChange={(evento) => alterar({ descricao: evento.target.value })}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo
          rotulo="Recorrência"
          htmlFor="janela-recorrencia"
          dica="Texto livre — “toda 1ª terça, 22h–02h”. A materialização automática das ocorrências é do motor de agendamento, fora desta fase."
        >
          <input
            id="janela-recorrencia"
            value={recorrencia}
            onChange={(evento) => alterar({ recorrencia: evento.target.value })}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {janela ? (
          <Campo
            rotulo="Situação"
            htmlFor="janela-situacao"
            dica="Janela cancelada não recebe mudança nova, e some da detecção de conflito — ela fica no calendário como registro do que não vai acontecer."
          >
            <select
              id="janela-situacao"
              value={situacao}
              onChange={(evento) => alterar({ situacao: evento.target.value as SituacaoDeJanela })}
              className={CLASSE_DE_CAMPO}
            >
              {SITUACOES_DE_JANELA.map((valor) => (
                <option key={valor} value={valor}>
                  {ROTULO_DE_JANELA[valor]}
                </option>
              ))}
            </select>
          </Campo>
        ) : null}

        {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

        {falha ? (
          <AvisoDeFalha
            falha={falha}
            aoRecarregar={() => void cliente.refetchQueries({ queryKey: chavesDeMudanca.raiz })}
          />
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={gravar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" loading={gravar.isPending} disabled={incompleto}>
            {janela ? 'Salvar' : 'Criar janela'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
